import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Button,
  Table,
  Form,
  Spinner,
  Alert,
  InputGroup,
  Badge,
} from 'react-bootstrap';
import {
  FaSearch,
  FaArrowLeft,
  FaChartLine,
  FaCalendarAlt,
  FaRupeeSign,
  FaChevronRight,
} from 'react-icons/fa';
import appConstants from '../constants/appConstants';
import Layout from '../components/Layout';

const AccountTypeSummary = () => {
  const navigate = useNavigate();
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [summary, setSummary] = useState([]);
  const [accountTypes, setAccountTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState({ show: false, message: '', variant: '' });
  const [navigationHistory, setNavigationHistory] = useState([]);
  const [currentAccountType, setCurrentAccountType] = useState(null);

  const fetchSummary = async () => {
    if (!startDate || !endDate) {
      setAlert({
        show: true,
        message: 'Please select both start and end dates.',
        variant: 'warning',
      });
      return;
    }

    setLoading(true);
    setAlert({ show: false, message: '', variant: '' });
    try {
      const token = localStorage.getItem('authToken');

      // Fetch all account types first
      const typesResponse = await axios.get(
        `${appConstants.API_ROOT_URL}get-account-types`,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      // Fetch summary data
      const summaryResponse = await axios.post(
        `${appConstants.API_ROOT_URL}get-account-type-summary`,
        { start_date: startDate, end_date: endDate },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      const allTypes = typesResponse.data.data || [];
      const summaryData = summaryResponse.data.summary || [];

      setAccountTypes(allTypes);

      // Filter to show only root account types (parent_id = null)
      // Match by account_type name since summary doesn't include account_type_id
      const rootSummary = summaryData
        .map((item) => {
          const accountType = allTypes.find(
            (t) => t.name === item.account_type,
          );
          return {
            ...item,
            account_type_id: accountType?.id,
            parent_id: accountType?.parent_id,
          };
        })
        .filter((item) => item.parent_id === null);

      setSummary(rootSummary);

      if (rootSummary.length === 0) {
        setAlert({
          show: true,
          message: 'No data found for the selected date range.',
          variant: 'info',
        });
      }
    } catch (err) {
      setAlert({
        show: true,
        message:
          err.response?.data?.message ||
          'Failed to fetch summary. Please try again.',
        variant: 'danger',
      });
    }
    setLoading(false);
  };

  const navigateToAccountType = (accountTypeId, accountTypeName) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('authToken');

      // Fetch summary data
      axios
        .post(
          `${appConstants.API_ROOT_URL}get-account-type-summary`,
          { start_date: startDate, end_date: endDate },
          { headers: { Authorization: `Bearer ${token}` } },
        )
        .then((summaryResponse) => {
          const summaryData = summaryResponse.data.summary || [];

          // Filter child account types for this parent
          const childTypesData = summaryData
            .map((item) => {
              const accountType = accountTypes.find(
                (t) => t.name === item.account_type,
              );
              return {
                ...item,
                account_type_id: accountType?.id,
                parent_id: accountType?.parent_id,
              };
            })
            .filter((item) => item.parent_id === accountTypeId);

          // Add to navigation history
          setNavigationHistory([...navigationHistory, currentAccountType]);
          setCurrentAccountType({ id: accountTypeId, name: accountTypeName });
          setSummary(childTypesData);
          setLoading(false);
        })
        .catch((err) => {
          setAlert({
            show: true,
            message:
              err.response?.data?.message ||
              'Failed to fetch details. Please try again.',
            variant: 'danger',
          });
          setLoading(false);
        });
    } catch (err) {
      setLoading(false);
    }
  };

  const navigateBack = () => {
    if (navigationHistory.length === 0) return;

    const previousAccountType = navigationHistory[navigationHistory.length - 1];
    const newHistory = navigationHistory.slice(0, -1);

    setNavigationHistory(newHistory);
    setCurrentAccountType(previousAccountType);

    if (previousAccountType === null) {
      // Back to root - fetch root summary
      fetchSummary();
    } else {
      // Back to previous level - fetch children of previous level
      setLoading(true);
      const token = localStorage.getItem('authToken');

      axios
        .post(
          `${appConstants.API_ROOT_URL}get-account-type-summary`,
          { start_date: startDate, end_date: endDate },
          { headers: { Authorization: `Bearer ${token}` } },
        )
        .then((summaryResponse) => {
          const summaryData = summaryResponse.data.summary || [];

          const childTypesData = summaryData
            .map((item) => {
              const accountType = accountTypes.find(
                (t) => t.name === item.account_type,
              );
              return {
                ...item,
                account_type_id: accountType?.id,
                parent_id: accountType?.parent_id,
              };
            })
            .filter((item) => item.parent_id === previousAccountType.id);

          setSummary(childTypesData);
          setLoading(false);
        })
        .catch((err) => {
          setAlert({
            show: true,
            message:
              err.response?.data?.message ||
              'Failed to fetch data. Please try again.',
            variant: 'danger',
          });
          setLoading(false);
        });
    }
  };
  const handleRowClick = (item) => {
    if (item.account_type_id) {
      navigateToAccountType(item.account_type_id, item.account_type);
    }
  };

  return (
    <Layout>
      <div
        style={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
          padding: '30px 20px',
        }}
      >
        {/* Header Card */}
        <div
          style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            borderRadius: '12px',
            padding: '30px',
            marginBottom: '30px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            color: 'white',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '15px',
            }}
          >
            <div style={{ flex: 1 }}>
              <h4
                style={{
                  margin: 0,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                }}
              >
                <FaChartLine style={{ fontSize: '1.8rem' }} />
                {currentAccountType
                  ? currentAccountType.name
                  : 'Account Type Summary'}
              </h4>
              <p style={{ margin: '8px 0 0 0', opacity: 0.9 }}>
                {currentAccountType
                  ? 'Sub-categories and their financial summary'
                  : 'View financial summary organized by account types'}
              </p>
            </div>
            <Button
              variant="outline-light"
              onClick={() => navigate(-1)}
              style={{
                borderRadius: '8px',
                padding: '8px 16px',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <FaArrowLeft /> Back
            </Button>
          </div>
        </div>

        {/* Alert Messages */}
        {alert.show && (
          <Alert
            variant={alert.variant}
            dismissible
            onClose={() => setAlert({ ...alert, show: false })}
            style={{
              borderRadius: '8px',
              marginBottom: '20px',
              border: 'none',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            }}
          >
            {alert.message}
          </Alert>
        )}

        {/* Filter Card */}
        <div
          style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '25px',
            marginBottom: '20px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          }}
        >
          <h6
            style={{
              fontWeight: '600',
              marginBottom: '20px',
              color: '#495057',
            }}
          >
            Select Date Range
          </h6>
          <div className="row g-3 align-items-end">
            <div className="col-12 col-md-4">
              <Form.Label
                style={{
                  fontWeight: '600',
                  color: '#495057',
                  fontSize: '0.9rem',
                  marginBottom: '8px',
                }}
              >
                Start Date
              </Form.Label>
              <InputGroup>
                <InputGroup.Text
                  style={{
                    backgroundColor: 'white',
                    borderRight: 'none',
                    borderRadius: '8px 0 0 8px',
                  }}
                >
                  <FaCalendarAlt style={{ color: '#667eea' }} />
                </InputGroup.Text>
                <Form.Control
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  style={{
                    borderLeft: 'none',
                    borderRadius: '0 8px 8px 0',
                    paddingLeft: '0',
                  }}
                />
              </InputGroup>
            </div>
            <div className="col-12 col-md-4">
              <Form.Label
                style={{
                  fontWeight: '600',
                  color: '#495057',
                  fontSize: '0.9rem',
                  marginBottom: '8px',
                }}
              >
                End Date
              </Form.Label>
              <InputGroup>
                <InputGroup.Text
                  style={{
                    backgroundColor: 'white',
                    borderRight: 'none',
                    borderRadius: '8px 0 0 8px',
                  }}
                >
                  <FaCalendarAlt style={{ color: '#667eea' }} />
                </InputGroup.Text>
                <Form.Control
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  style={{
                    borderLeft: 'none',
                    borderRadius: '0 8px 8px 0',
                    paddingLeft: '0',
                  }}
                />
              </InputGroup>
            </div>
            <div className="col-12 col-md-4">
              <Button
                onClick={fetchSummary}
                disabled={loading}
                style={{
                  width: '100%',
                  borderRadius: '8px',
                  padding: '10px 20px',
                  fontWeight: '600',
                  background:
                    'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  border: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                }}
              >
                {loading ? (
                  <>
                    <Spinner animation="border" size="sm" />
                    Loading...
                  </>
                ) : (
                  <>
                    <FaSearch /> Get Summary
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>

        {/* Summary Table Card */}
        {summary.length > 0 && (
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '30px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '20px',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                }}
              >
                {navigationHistory.length > 0 && (
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={navigateBack}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '4px 8px',
                      borderRadius: '8px',
                    }}
                  >
                    <FaArrowLeft />
                  </Button>
                )}
                <h6
                  style={{
                    fontWeight: '600',
                    margin: 0,
                    color: '#495057',
                  }}
                >
                  {currentAccountType
                    ? currentAccountType.name
                    : 'Summary Report'}
                </h6>
              </div>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <Table hover responsive style={{ marginBottom: 0 }}>
                <thead
                  style={{
                    backgroundColor: '#f8f9fa',
                    borderBottom: '2px solid #dee2e6',
                  }}
                >
                  <tr>
                    <th
                      style={{
                        padding: '12px',
                        fontWeight: '600',
                        color: '#495057',
                      }}
                    >
                      Account Type
                    </th>
                    <th
                      style={{
                        padding: '12px',
                        fontWeight: '600',
                        color: '#495057',
                        textAlign: 'right',
                      }}
                    >
                      Debit Total
                    </th>
                    <th
                      style={{
                        padding: '12px',
                        fontWeight: '600',
                        color: '#495057',
                        textAlign: 'right',
                      }}
                    >
                      Credit Total
                    </th>
                    <th
                      style={{
                        padding: '12px',
                        fontWeight: '600',
                        color: '#495057',
                        textAlign: 'right',
                      }}
                    >
                      Balance
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {summary.map((item, index) => {
                    const balance = (
                      item.total_debit - item.total_credit
                    ).toFixed(2);
                    return (
                      <tr
                        key={index}
                        onClick={() => handleRowClick(item)}
                        style={{
                          borderBottom: '1px solid #f0f0f0',
                          cursor: 'pointer',
                          transition: 'background-color 0.2s',
                        }}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.backgroundColor = '#f8f9fa')
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.backgroundColor = 'white')
                        }
                      >
                        <td
                          style={{
                            padding: '12px',
                            fontSize: '0.9rem',
                            fontWeight: '500',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                          }}
                        >
                          <FaChevronRight
                            style={{ color: '#667eea', fontSize: '0.8rem' }}
                          />
                          {item.account_type}
                        </td>
                        <td
                          style={{
                            padding: '12px',
                            fontSize: '0.9rem',
                            textAlign: 'right',
                            color: '#28a745',
                            fontWeight: '500',
                          }}
                        >
                          <FaRupeeSign style={{ fontSize: '0.8rem' }} />
                          {parseFloat(item.total_debit).toLocaleString(
                            'en-IN',
                            {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            },
                          )}
                        </td>
                        <td
                          style={{
                            padding: '12px',
                            fontSize: '0.9rem',
                            textAlign: 'right',
                            color: '#dc3545',
                            fontWeight: '500',
                          }}
                        >
                          <FaRupeeSign style={{ fontSize: '0.8rem' }} />
                          {parseFloat(item.total_credit).toLocaleString(
                            'en-IN',
                            {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            },
                          )}
                        </td>
                        <td
                          style={{
                            padding: '12px',
                            fontSize: '0.9rem',
                            textAlign: 'right',
                            fontWeight: '600',
                            color: balance >= 0 ? '#28a745' : '#dc3545',
                          }}
                        >
                          <FaRupeeSign style={{ fontSize: '0.8rem' }} />
                          {parseFloat(balance).toLocaleString('en-IN', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          </div>
        )}

        {/* No Child Account Types Message */}
        {!loading &&
          summary.length === 0 &&
          currentAccountType !== null &&
          startDate &&
          endDate && (
            <div
              style={{
                background: 'white',
                borderRadius: '12px',
                padding: '30px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  marginBottom: '20px',
                }}
              >
                {navigationHistory.length > 0 && (
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={navigateBack}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '4px 8px',
                      borderRadius: '8px',
                    }}
                  >
                    <FaArrowLeft />
                  </Button>
                )}
                <h6
                  style={{
                    fontWeight: '600',
                    margin: 0,
                    color: '#495057',
                  }}
                >
                  {currentAccountType.name}
                </h6>
              </div>
              <Alert variant="info" style={{ borderRadius: '8px', margin: 0 }}>
                <div style={{ textAlign: 'center' }}>
                  <FaChartLine
                    style={{
                      fontSize: '3rem',
                      color: '#667eea',
                      marginBottom: '15px',
                    }}
                  />
                  <h5 style={{ marginBottom: '10px' }}>
                    No Sub-Categories Found
                  </h5>
                  <p style={{ marginBottom: 0, color: '#6c757d' }}>
                    The account type "{currentAccountType.name}" has no child
                    account types in the selected date range.
                  </p>
                </div>
              </Alert>
            </div>
          )}
      </div>
    </Layout>
  );
};

export default AccountTypeSummary;
